<script setup>
import { computed } from 'vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import { CSAT_RATINGS } from 'shared/constants/messages';
import { formatMetricWithCount } from 'dashboard/helper/reportMetricHelper';

// Distribuição das notas de UMA escala (Csat::ScoreSummary.by_scale). Em 1 a 5
// é a barra de sempre do relatório (carinhas e as cores do CSAT_RATINGS); em
// outra escala os pontos viram números, do pior (laranja) ao melhor (verde).
const props = defineProps({
  // { scale_min, scale_max, count, ratings_count: { valor: quantidade } }
  scale: { type: Object, required: true },
});

const isDefaultScale = computed(
  () => props.scale.scale_min === 1 && props.scale.scale_max === 5
);

// Hex de propósito: o chart.js não lê classe Tailwind, e a barra de 1 a 5 já
// usava as cores do CSAT_RATINGS. Os extremos são as mesmas cores dela.
const LOW = [0xfd, 0xad, 0x2a];
const HIGH = [0x44, 0xce, 0x4b];
const blend = fraction =>
  `#${LOW.map((low, i) =>
    Math.round(low + (HIGH[i] - low) * fraction)
      .toString(16)
      .padStart(2, '0')
  ).join('')}`;

// Do maior para o menor, como a barra sempre mostrou (😍 primeiro).
const points = computed(() => {
  const { scale_min: min, scale_max: max } = props.scale;
  return Array.from(
    { length: max - min + 1 },
    (_value, offset) => max - offset
  ).map(value => {
    const rating = isDefaultScale.value
      ? CSAT_RATINGS.find(item => item.value === value)
      : null;
    return {
      value,
      label: rating ? rating.emoji : String(value),
      color: rating ? rating.color : blend((value - min) / (max - min)),
      count: props.scale.ratings_count?.[value] || 0,
    };
  });
});

const percent = count =>
  props.scale.count ? (count * 100) / props.scale.count : 0;

const chartData = computed(() => ({
  labels: ['Rating'],
  datasets: points.value.map(point => ({
    label: point.label,
    data: [percent(point.count)],
    backgroundColor: point.color,
  })),
}));

const chartOptions = {
  indexAxis: 'y',
  responsive: true,
  plugins: {
    legend: { display: false },
    title: { display: false },
    tooltip: { enabled: false },
  },
  scales: {
    x: { display: false, stacked: true, max: 100 },
    y: { display: false, stacked: true },
  },
};
</script>

<template>
  <div class="flex flex-col gap-2 min-w-0 rtl:[direction:initial]">
    <div
      class="flex flex-wrap items-center text-xs font-medium md:text-sm text-n-slate-12 gap-x-4 gap-y-1"
    >
      <span
        v-for="point in points"
        :key="point.value"
        class="inline-flex items-center gap-1"
      >
        <span v-if="isDefaultScale" class="mx-0.5">{{ point.label }}</span>
        <!-- Nota numa etiqueta: "10 35%" solto lia como "1035%". -->
        <span
          v-else
          class="px-1.5 rounded tabular-nums bg-n-alpha-2 text-n-slate-12"
        >
          {{ point.label }}
        </span>
        <!-- 1 a 5: "48,0% · 12/25", como sempre. Escala maior: só a % (10
             pontos com contagem quebravam a legenda em duas linhas). -->
        <span v-if="isDefaultScale">
          {{ formatMetricWithCount(point.count, scale.count) }}
        </span>
        <span v-else class="text-n-slate-11">
          {{ Math.round(percent(point.count)) }}%
        </span>
      </span>
    </div>
    <div class="h-6">
      <BarChart :collection="chartData" :chart-options="chartOptions" />
    </div>
  </div>
</template>
